import React, { useState, useEffect } from 'react';
import { Typography, Spin } from 'antd';
import { useAuthStore, useChannelStore, useChannelMemberStore, useAgentStore, useServerStore } from '../stores';
import { useCan } from '../hooks/usePermissions';
import { addChannelMember, removeChannelMember, setChannelMemberRequireMentionPolicy } from '../lib/api';
import type { ChannelMember, RequireMentionPolicy } from '../lib/api';
import { UserBadge } from './UserBadge';
import TurnRoutingPolicyControl from './TurnRoutingPolicyControl';

interface Props {
  channelId: string;
}

export default function ServerMembersPanel({ channelId }: Props) {
  const currentUser = useAuthStore(s => s.currentUser);
  const agents = useAgentStore(s => s.agents);
  const allMembers = useServerStore(s => s.members);
  const channels = useChannelStore(s => s.channels);
  const bumpChannelMembersVersion = useChannelStore(s => s.bumpChannelMembersVersion);
  const membersVersion = useChannelStore(s => s.channelMembersVersion[channelId] ?? 0);
  const loadChannelMembers = useChannelMemberStore(s => s.loadChannelMembers);
  const channel = channels.find(c => c.id === channelId);
  const [channelMemberIds, setChannelMemberIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [savingPolicy, setSavingPolicy] = useState<string | null>(null);
  const [channelMemberData, setChannelMemberData] = useState<Map<string, ChannelMember>>(new Map());

  const channelCreatedBy = channel?.created_by ?? '';
  const canManageMembers = useCan('channel.manage_members', channelId);
  const turnRoutingEligible =
    channel?.is_member === true &&
    channel.type !== 'dm' &&
    (channel.channel_type === undefined || channel.channel_type === 0 || channel.channel_type === 11);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadChannelMembers(channelId, membersVersion).then(members => {
      if (!cancelled) {
        setChannelMemberIds(new Set(members.map(m => m.user_id)));
        setChannelMemberData(new Map(members.map(m => [m.user_id, m])));
        setLoading(false);
      }
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [channelId, membersVersion, loadChannelMembers]);


  const handleAdd = async (userId: string) => {
    setAdding(userId);
    try {
      await addChannelMember(channelId, userId);
      setChannelMemberIds(prev => new Set([...prev, userId]));
      // Sync channelMemberData so subsequent operations (e.g. require-mention
      // toggle) find the member in the Map. Build a default entry from the
      // server-store member data available locally.
      const serverMember = allMembers.find(m => m.user_id === userId);
      if (serverMember) {
        setChannelMemberData(prev => {
          const next = new Map(prev);
          next.set(userId, { ...serverMember, require_mention_policy: 'inherit', effective_require_mention: serverMember.role === 'agent' });
          return next;
        });
      }
      bumpChannelMembersVersion(channelId);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to add member');
    } finally {
      setAdding(null);
    }
  };

  const handleRemove = async (userId: string) => {
    setRemoving(userId);
    try {
      await removeChannelMember(channelId, userId);
      setChannelMemberIds(prev => { const next = new Set(prev); next.delete(userId); return next; });
      bumpChannelMembersVersion(channelId);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to remove member');
    } finally {
      setRemoving(null);
    }
  };

  const handlePolicyChange = async (userId: string, policy: RequireMentionPolicy) => {
    setSavingPolicy(userId);
    // Optimistic update: reflect the change immediately so the checkbox
    // doesn't snap back to the old value while the API call is in flight.
    const optimisticEffective = policy === 'on';
    const prevMemberData = channelMemberData.get(userId);
    setChannelMemberData(prev => {
      const next = new Map(prev);
      const existing = next.get(userId);
      if (existing) {
        next.set(userId, { ...existing, require_mention_policy: policy, effective_require_mention: optimisticEffective });
      }
      return next;
    });
    try {
      const state = await setChannelMemberRequireMentionPolicy(channelId, userId, policy);
      // Reconcile with server response (handles 'inherit' resolution correctly).
      setChannelMemberData(prev => {
        const next = new Map(prev);
        const existing = next.get(userId);
        if (existing) {
          next.set(userId, { ...existing, require_mention_policy: state.require_mention_policy, effective_require_mention: state.effective_require_mention });
        }
        return next;
      });
    } catch (err) {
      // Revert optimistic update on failure.
      if (prevMemberData) {
        setChannelMemberData(prev => {
          const next = new Map(prev);
          next.set(userId, prevMemberData);
          return next;
        });
      }
      alert(err instanceof Error ? err.message : 'Failed to update policy');
    } finally {
      setSavingPolicy(null);
    }
  };

  const inChannel = allMembers.filter(u => channelMemberIds.has(u.user_id));
  const notInChannel = allMembers.filter(u => !channelMemberIds.has(u.user_id));

  const renderUser = (u: ChannelMember, isMember: boolean) => {
    const isCurrentUser = u.user_id === currentUser?.id;
    const isCreator = u.user_id === channelCreatedBy;
    const memberData = isMember ? channelMemberData.get(u.user_id) : undefined;
    const agentData = u.role === 'agent' ? agents.find(a => a.id === u.user_id) : undefined;

    return (
      <div
        key={u.user_id}
        className="member-row"
        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <UserBadge
            userId={u.user_id}
            displayName={u.display_name}
            role={u.role}
            avatarUrl={u.avatar_url}
            state={agentData?.state}
            reason={memberData?.reason}
            size={28}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          {isMember && u.role === 'agent' && canManageMembers && (
            <label title="When checked, this agent only responds when @mentioned" style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#999', cursor: 'pointer' }}>
              <span>@</span>
              <input
                type="checkbox"
                checked={memberData?.effective_require_mention ?? true}
                disabled={savingPolicy === u.user_id}
                onChange={e => void handlePolicyChange(u.user_id, e.target.checked ? 'on' : 'off')}
              />
            </label>
          )}
          {canManageMembers && !isCurrentUser && !isCreator && (
            isMember ? (
              <button
                className="icon-btn"
                title="Remove from this channel"
                onClick={() => handleRemove(u.user_id)}
                disabled={removing === u.user_id}
                style={{ fontSize: 14, color: 'var(--danger, #d32f2f)' }}
              >−</button>
            ) : (
              <button
                className="icon-btn"
                title="Add to this channel"
                onClick={() => handleAdd(u.user_id)}
                disabled={adding === u.user_id}
                style={{ fontSize: 14, color: 'var(--text-secondary)' }}
              >+</button>
            )
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="channel-members-panel">
        <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
      </div>
    );
  }

  return (
    <div className="channel-members-panel">
      <TurnRoutingPolicyControl channelId={channelId} eligible={turnRoutingEligible} />
      <div data-testid="channel-member-roster" style={{ marginBottom: 12 }}>
        <Typography.Text strong style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#999' }}>
          In this channel — {inChannel.length}
        </Typography.Text>
        <div style={{ marginTop: 4 }}>
          {inChannel.length === 0 && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>No members yet</Typography.Text>
          )}
          {inChannel.map(u => renderUser(u, true))}
        </div>
      </div>

      {notInChannel.length > 0 && (
        <div>
          <Typography.Text strong style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#999' }}>
            Not in this channel
          </Typography.Text>
          <div style={{ marginTop: 4 }}>
            {notInChannel.map(u => renderUser(u, false))}
          </div>
        </div>
      )}
    </div>
  );
}
